import React, { useState, useEffect } from 'react';
import { siteService } from '../services/siteService';
import { customerService } from '../services/customerService';
import CustomerModal from './CustomerModal';
import SiteModal from './SiteModal';

const SiteContactModal = ({ contact, onSave, onClose, defaultSiteId }) => {
  const [formData, setFormData] = useState({
    site_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    position: '',
  });
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [showNewSiteModal, setShowNewSiteModal] = useState(false);

  const isEditing = !!contact;

  useEffect(() => {
    siteService.getSites().then(res => setSites((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    customerService.getCustomers().then(res => setCustomers((res.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  useEffect(() => {
    if (contact) {
      const matchingSite = sites.find(s => String(s.id) === String(contact.site_id));
      setSelectedCustomer(matchingSite ? String(matchingSite.customer_id) : '');
      setFormData({
        site_id: contact.site_id || '',
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        position: contact.position || '',
      });
    } else if (defaultSiteId && sites.length > 0) {
      const matchingSite = sites.find(s => String(s.id) === String(defaultSiteId));
      if (matchingSite) {
        setSelectedCustomer(String(matchingSite.customer_id));
        setFormData(prev => ({ ...prev, site_id: String(defaultSiteId) }));
      }
    }
  }, [contact, sites, defaultSiteId]);

  const filteredSites = selectedCustomer
    ? sites.filter(s => String(s.customer_id) === selectedCustomer)
    : sites;

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setFormData(prev => ({ ...prev, site_id: '' }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      await onSave(formData);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: error.response?.data?.message || 'An error occurred. Please try again.' });
      }
    }

    setLoading(false);
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-300 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-600 ${
      errors[field]
        ? 'border-red-500 focus:border-red-500'
        : 'border-gray-200 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'
    }`;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditing ? 'Edit Site Contact' : 'Add New Site Contact'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl font-semibold leading-none transition-colors">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {errors.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          {/* Customer filter */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Customer</label>
            <div className="flex gap-2">
              <select value={selectedCustomer} onChange={handleCustomerChange} className={inputClass('customer_id') + ' flex-1'}>
                <option value="">All customers...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setShowNewCustomerModal(true)}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap"
              >+ New</button>
            </div>
          </div>

          {/* Site select */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Site <span className="text-red-500">*</span></label>
            <div className="flex gap-2">
              <select name="site_id" value={formData.site_id} onChange={handleChange} disabled={!selectedCustomer} className={inputClass('site_id') + ' flex-1'}>
                <option value="">Select a site...</option>
                {filteredSites.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewSiteModal(true)}
                disabled={!selectedCustomer}
                className="px-3 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >+ New</button>
            </div>
            {errors.site_id && <p className="text-red-500 dark:text-red-400 text-sm">{errors.site_id[0]}</p>}
          </div>

          {/* First/Last name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">First Name <span className="text-red-500">*</span></label>
              <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First name" disabled={!formData.site_id} className={inputClass('first_name')} />
              {errors.first_name && <p className="text-red-500 dark:text-red-400 text-sm">{errors.first_name[0]}</p>}
            </div>
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-gray-900 dark:text-white">Last Name</label>
              <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last name" disabled={!formData.site_id} className={inputClass('last_name')} />
              {errors.last_name && <p className="text-red-500 dark:text-red-400 text-sm">{errors.last_name[0]}</p>}
            </div>
          </div>

          {/* Position */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Position</label>
            <input type="text" name="position" value={formData.position} onChange={handleChange} placeholder="Job title / position" disabled={!formData.site_id} className={inputClass('position')} />
            {errors.position && <p className="text-red-500 dark:text-red-400 text-sm">{errors.position[0]}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email address" disabled={!formData.site_id} className={inputClass('email')} />
            {errors.email && <p className="text-red-500 dark:text-red-400 text-sm">{errors.email[0]}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-1">
            <label className="block text-sm font-semibold text-gray-900 dark:text-white">Phone</label>
            <input type="text" name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone number" disabled={!formData.site_id} className={inputClass('phone')} />
            {errors.phone && <p className="text-red-500 dark:text-red-400 text-sm">{errors.phone[0]}</p>}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed">
              {loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Add Contact')}
            </button>
          </div>
        </form>
      </div>
    </div>

    {showNewCustomerModal && (
      <CustomerModal
        onSave={async (data) => {
          const res = await customerService.createCustomer(data);
          const newCustomer = res.data;
          const updated = await customerService.getCustomers();
          setCustomers((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setSelectedCustomer(String(newCustomer.id));
          setFormData(prev => ({ ...prev, site_id: '' }));
          setShowNewCustomerModal(false);
        }}
        onClose={() => setShowNewCustomerModal(false)}
      />
    )}
    {showNewSiteModal && (
      <SiteModal
        site={{ customer_id: selectedCustomer }}
        onSave={async (data) => {
          const res = await siteService.createSite(data);
          const newSite = res.data;
          const updated = await siteService.getSites();
          setSites((updated.data || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
          setFormData(prev => ({ ...prev, site_id: String(newSite.id) }));
          setShowNewSiteModal(false);
        }}
        onClose={() => setShowNewSiteModal(false)}
      />
    )}
    </>
  );
};

export default SiteContactModal;
